import { useMemo } from 'react'
import { realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { useMetroMapDispatch } from './useMetroMapStore'
import { useRouteSelection } from './useRouteSelection'

export interface RouteCardEdits {
  readonly onSwap: () => void
  readonly onFromClear: () => void
  readonly onToClear: () => void
}

/**
 * The RouteCard's swap and clear-a-slot taps — the store dispatch plus the
 * cross-platform events, shared by the phone overlay and the desktop side
 * panel so the two cannot drift (2026-10-28; until then both actions were
 * visible only as anonymous autocapture clicks, on every platform):
 *
 * - `route_card_swap_destinations_tapped { station_from_id, station_to_id }`
 *   — the ends BEFORE the swap (the name iOS V1 used; Amplitude history
 *   continues);
 * - `route_card_clear_tapped { slot: from | to, station_id }` — the station
 *   being cleared.
 *
 * Only these taps report: a programmatic clear (closing the card, a new
 * route from a station card) goes straight to the store.
 */
export function useRouteCardEdits(): RouteCardEdits {
  const dispatch = useMetroMapDispatch()
  const { fromStation, toStation } = useRouteSelection()
  return useMemo(
    () => ({
      onSwap: () => {
        realMetroAnalyticsReporter('route_card_swap_destinations_tapped', {
          station_from_id: fromStation?.id ?? '',
          station_to_id: toStation?.id ?? '',
        })
        dispatch({ kind: 'RouteSelectionSwapped' })
      },
      onFromClear: () => {
        realMetroAnalyticsReporter('route_card_clear_tapped', {
          slot: 'from',
          station_id: fromStation?.id ?? '',
        })
        dispatch({ kind: 'RouteFromCleared' })
      },
      onToClear: () => {
        realMetroAnalyticsReporter('route_card_clear_tapped', {
          slot: 'to',
          station_id: toStation?.id ?? '',
        })
        dispatch({ kind: 'RouteToCleared' })
      },
    }),
    [dispatch, fromStation, toStation],
  )
}
